<!-- New Case Wizard -->
<div class="sp-dashboard-container">
  <sp-header></sp-header>

  <main class="sp-main">

    <!-- Page header -->
    <div class="sp-page-header">
      <div class="sp-greeting">
        <div class="breadcrumb">
          <a (click)="router.navigate(['/case-management'])" class="crumb">Dashboard</a>
          <span class="crumb-sep">/</span>
          <a (click)="router.navigate(['/case-management/cases'])" class="crumb">All Cases</a>
          <span class="crumb-sep">/</span>
          <span class="crumb crumb-active">New Case</span>
        </div>
        <h1><span class="material-icons page-icon">add_circle</span> Open New Legal Case</h1>
        <p>Step {{ currentStep }} of {{ totalSteps }}: <strong>{{ stepLabel(currentStep) }}</strong></p>
      </div>
      <div class="sp-header-actions">
        <button class="btn btn-secondary" (click)="cancel()">
          <span class="material-icons">close</span> Cancel
        </button>
      </div>
    </div>

    <!-- Stepper -->
    <nav class="stepper">
      <div class="step-pill"
           *ngFor="let s of [1,2,3,4,5]"
           [class.active]="currentStep === s"
           [class.complete]="currentStep > s"
           [class.clickable]="s <= currentStep"
           (click)="jumpTo(s)">
        <div class="step-num">
          <span class="material-icons" *ngIf="currentStep > s">check</span>
          <span *ngIf="currentStep <= s">{{ s }}</span>
        </div>
        <div class="step-name">{{ stepLabel(s) }}</div>
      </div>
    </nav>

    <!-- Error message -->
    <div class="error-banner" *ngIf="errorMessage">
      <span class="material-icons">warning</span> {{ errorMessage }}
    </div>

    <!-- Form panels -->
    <section class="wizard-panel">

      <!-- ─── STEP 1 — Source reference ─── -->
      <div *ngIf="currentStep === 1" [formGroup]="step1Form" class="step-body">
        <div class="step-intro">
          <h2>Where is this case coming from?</h2>
          <p>Most legal matters originate from an existing claim, loan, contract or policy.
            Linking the source record now means all relevant context follows the case.</p>
        </div>

        <div class="form-row">
          <div class="form-field">
            <label>Source type *</label>
            <div class="source-type-grid">
              <label class="source-type-card"
                     *ngFor="let st of sourceTypes"
                     [class.selected]="step1Form.value.sourceType === st.value">
                <input type="radio" formControlName="sourceType" [value]="st.value" />
                <div class="stc-name">{{ st.label }}</div>
              </label>
            </div>
          </div>
        </div>

        <div class="form-row" *ngIf="step1Form.value.sourceType !== 'STANDALONE'">
          <div class="form-field">
            <label>Reference number</label>
            <div class="ref-modes">
              <label><input type="radio" name="refMode" [checked]="refMode === 'manual'" (change)="setRefMode('manual')"> Enter manually</label>
              <label><input type="radio" name="refMode" [checked]="refMode === 'lookup'" (change)="setRefMode('lookup')"> Look up in repository</label>
              <label><input type="radio" name="refMode" [checked]="refMode === 'generate'" (change)="setRefMode('generate')"> System-generated</label>
            </div>

            <input *ngIf="refMode === 'manual'" type="text" formControlName="sourceRef"
                   placeholder="e.g. CLM-2026-08-1234" />

            <div *ngIf="refMode === 'lookup'" class="lookup-group">
              <input type="text" formControlName="sourceRef" readonly
                     placeholder="Pick a folder or document from the repository…" />
              <button class="lookup-btn" type="button" (click)="openRefBrowser()">
                <span class="material-icons">folder_open</span> Browse
              </button>
            </div>

            <small class="form-hint" *ngIf="refMode === 'generate'">
              The system will assign the case number (e.g. LGL-2026-00001) when you open the case.
            </small>

            <div class="ref-browser" *ngIf="refMode === 'lookup' && refBrowsing">
              <div class="rb-crumbs">
                <ng-container *ngFor="let c of refCrumbs; let i = index">
                  <a (click)="refCrumbTo(i)">{{ c.name }}</a>
                  <span class="sep" *ngIf="i < refCrumbs.length - 1">/</span>
                </ng-container>
                <button type="button" class="rb-usefolder" *ngIf="refCrumbs.length > 1" (click)="useCurrentFolder()">
                  Use this folder name
                </button>
              </div>
              <div class="rb-list">
                <div *ngIf="refLoading" class="muted">Loading…</div>
                <div class="rb-row" *ngFor="let n of refNodes">
                  <a class="rb-fold" *ngIf="n.isFolder" (click)="enterRef(n)">
                    <span class="material-icons">folder</span>{{ n.name }}
                  </a>
                  <span class="rb-file" *ngIf="!n.isFolder">
                    <span class="material-icons">description</span>{{ n.name }}
                    <button type="button" class="rb-pick" (click)="pickRef(n)">Use</button>
                  </span>
                </div>
                <div *ngIf="!refLoading && !refNodes.length" class="muted">This folder is empty.</div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-field">
            <label>Brief description (optional)</label>
            <textarea formControlName="sourceDescription" rows="3"
                      placeholder="One-line context that helps anyone reading this case understand its origin..."></textarea>
          </div>
        </div>
      </div>

      <!-- ─── STEP 2 — Case details ─── -->
      <div *ngIf="currentStep === 2" [formGroup]="step2Form" class="step-body">
        <div class="step-intro">
          <h2>Case details</h2>
          <p>Core information about this matter — type, priority, exposure, and who's running it.</p>
        </div>

        <div class="form-row">
          <div class="form-field full">
            <label>Case title *</label>
            <input type="text" formControlName="title" [class.invalid]="ctrlInvalid(step2Form,'title')"
                   placeholder="e.g. Subrogation: Motor TPP — Mombasa Rd RTA, Reg KCB 234A" />
            <small class="form-hint">A descriptive title visible across all lists and reports.</small>
            <small class="field-error" *ngIf="ctrlInvalid(step2Form,'title')">{{ ctrlError(step2Form,'title') }}</small>
          </div>
        </div>

        <div class="form-row two-col">
          <div class="form-field">
            <label>Case type *</label>
            <select formControlName="type">
              <option *ngFor="let t of caseTypes" [value]="t">{{ TYPE_LABELS[t] }}</option>
            </select>
          </div>
          <div class="form-field">
            <label>Priority *</label>
            <select formControlName="priority">
              <option *ngFor="let p of priorities" [value]="p">{{ p }}</option>
            </select>
          </div>
        </div>

        <div class="form-row two-col">
          <div class="form-field">
            <label>Our role *</label>
            <div class="role-toggle">
              <label class="role-option"
                     *ngFor="let r of roles"
                     [class.selected]="step2Form.value.ourRole === r">
                <input type="radio" formControlName="ourRole" [value]="r" />
                <span class="material-icons">{{ r === 'PLAINTIFF' ? 'gavel' : 'shield' }}</span>
                <strong>{{ r }}</strong>
                <small>{{ r === 'PLAINTIFF' ? 'Bringing the case' : 'Being sued / defending' }}</small>
              </label>
            </div>
          </div>
          <div class="form-field">
            <label>Exposure amount (KES) *</label>
            <input type="number" formControlName="exposureAmount" min="0" placeholder="0"
                   [class.invalid]="ctrlInvalid(step2Form,'exposureAmount')" />
            <small class="form-hint">Amount at stake — recoverable or potential liability.</small>
            <small class="field-error" *ngIf="ctrlInvalid(step2Form,'exposureAmount')">{{ ctrlError(step2Form,'exposureAmount') }}</small>
          </div>
        </div>

        <div class="form-row two-col">
          <div class="form-field">
            <label>Court / forum</label>
            <input type="text" formControlName="court" list="court-list"
                   placeholder="Type or pick from the list..." />
            <datalist id="court-list">
              <option *ngFor="let c of courtList" [value]="c.label"></option>
            </datalist>
          </div>
          <div class="form-field">
            <label>Court case number</label>
            <input type="text" formControlName="courtCaseNumber"
                   placeholder="e.g. CMCC NO. 1234 OF 2026" />
          </div>
        </div>

        <div class="form-row two-col">
          <div class="form-field assignee-field">
            <label>Assigned to *</label>
            <input type="text" formControlName="assignedTo" autocomplete="off"
                   [class.invalid]="ctrlInvalid(step2Form,'assignedTo')"
                   placeholder="Search a user by name…"
                   (input)="onAssigneeInput($any($event.target).value)" />
            <div class="user-results" *ngIf="userResults.length">
              <button type="button" class="user-opt" *ngFor="let u of userResults" (click)="selectAssignee(u)">
                <span class="material-icons">person</span>
                <span class="uo-name">{{ u.displayName }}</span>
                <span class="uo-mail" *ngIf="u.email">{{ u.email }}</span>
              </button>
            </div>
            <small class="form-hint" *ngIf="userSearching">Searching…</small>
            <small class="field-error" *ngIf="ctrlInvalid(step2Form,'assignedTo')">{{ ctrlError(step2Form,'assignedTo') }}</small>
          </div>
          <div class="form-field">
            <label>Expected close date</label>
            <input type="date" formControlName="expectedCloseDate" />
          </div>
        </div>
      </div>

      <!-- ─── STEP 3 — Parties ─── -->
      <div *ngIf="currentStep === 3" [formGroup]="step3Form" class="step-body">
        <div class="step-intro">
          <h2>Parties</h2>
          <p>The opposing party is required. Counsels, witnesses, experts can be added now or later.</p>
        </div>

        <div class="form-row two-col">
          <div class="form-field">
            <label>Opposing party *</label>
            <input type="text" formControlName="opposingParty" [class.invalid]="ctrlInvalid(step3Form,'opposingParty')"
                   placeholder="Name of person or company on the other side" />
            <small class="field-error" *ngIf="ctrlInvalid(step3Form,'opposingParty')">{{ ctrlError(step3Form,'opposingParty') }}</small>
          </div>
          <div class="form-field">
            <label>Opposing counsel (law firm)</label>
            <input type="text" formControlName="opposingCounsel"
                   placeholder="e.g. Karanja &amp; Co Advocates" />
          </div>
        </div>

        <div class="parties-section">
          <div class="parties-section-header">
            <h3>Additional parties (witnesses, experts, etc.)</h3>
            <button class="btn btn-secondary" type="button" (click)="addParty()">
              <span class="material-icons">person_add</span> Add Party
            </button>
          </div>

          <div class="party-rows" formArrayName="additionalParties">
            <div class="party-row-form"
                 *ngFor="let pCtrl of additionalParties.controls; let i = index"
                 [formGroupName]="i">
              <div class="form-row four-col">
                <div class="form-field">
                  <label>Role</label>
                  <select formControlName="role">
                    <option *ngFor="let r of partyRoles" [value]="r">{{ r }}</option>
                  </select>
                </div>
                <div class="form-field">
                  <label>Type</label>
                  <select formControlName="type">
                    <option *ngFor="let t of partyTypes" [value]="t">{{ t }}</option>
                  </select>
                </div>
                <div class="form-field span2">
                  <label>Name</label>
                  <input type="text" formControlName="name" placeholder="Full name" />
                </div>
              </div>
              <div class="form-row two-col">
                <div class="form-field">
                  <label>Contact</label>
                  <input type="text" formControlName="contact" placeholder="Phone" />
                </div>
                <div class="form-field">
                  <label>Email</label>
                  <input type="email" formControlName="email" placeholder="email@example.com" />
                </div>
              </div>
              <button class="remove-party-btn" type="button" (click)="removeParty(i)" title="Remove">
                <span class="material-icons">delete</span>
              </button>
            </div>

            <div class="empty-state" *ngIf="additionalParties.length === 0">
              <span class="material-icons">group_off</span>
              <p>No additional parties yet. The opposing party above is enough to proceed.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- ─── STEP 4 — First event ─── -->
      <div *ngIf="currentStep === 4" [formGroup]="step4Form" class="step-body">
        <div class="step-intro">
          <h2>Schedule the first event</h2>
          <p>Most cases have a known first date — a mention, a filing deadline, a settlement meeting.
            Reminders will fire automatically based on the days-before list you set.</p>
        </div>

        <label class="skip-toggle">
          <input type="checkbox" formControlName="skipFirstEvent" />
          <span>Skip this — I'll schedule events later</span>
        </label>

        <div class="event-fields" *ngIf="!step4Form.value.skipFirstEvent">
          <div class="form-row two-col">
            <div class="form-field">
              <label>Event title *</label>
              <input type="text" formControlName="title"
                     placeholder="e.g. First mention, Pretrial conference" />
            </div>
            <div class="form-field">
              <label>Event type *</label>
              <select formControlName="type">
                <option *ngFor="let t of eventTypes" [value]="t">{{ EVENT_LABELS[t] }}</option>
              </select>
            </div>
          </div>

          <div class="form-row two-col">
            <div class="form-field">
              <label>Date &amp; time *</label>
              <input type="datetime-local" formControlName="scheduledAt" />
            </div>
            <div class="form-field">
              <label>Duration (minutes)</label>
              <input type="number" formControlName="durationMinutes" min="15" step="15" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label>Location *</label>
              <input type="text" formControlName="location"
                     placeholder="e.g. Milimani CMCC Court 7" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-field">
              <label>Reminders (days before)</label>
              <input type="text" formControlName="reminderDaysBefore" placeholder="e.g. 7,3,1" />
              <small class="form-hint">Comma-separated. Reminders fire to assignee email + in-app.</small>
            </div>
          </div>
        </div>
      </div>

      <!-- ─── STEP 5 — Summary / Review ─── -->
      <div *ngIf="currentStep === 5" class="step-body">
        <div class="step-intro">
          <h2>Review &amp; Open Case</h2>
          <p>Confirm everything below — clicking Open Case will create the matter and generate the case number.</p>
        </div>

        <div class="summary-blocks">
          <div class="summary-block">
            <h3><span class="material-icons">link</span> Source</h3>
            <div class="sb-row"><span>Type:</span><strong>{{ summaryData.s1.sourceType }}</strong></div>
            <div class="sb-row" *ngIf="summaryData.s1.sourceRef"><span>Reference:</span><strong>{{ summaryData.s1.sourceRef }}</strong></div>
            <div class="sb-row" *ngIf="summaryData.s1.sourceDescription"><span>Description:</span><span class="text-wrap">{{ summaryData.s1.sourceDescription }}</span></div>
          </div>

          <div class="summary-block">
            <h3><span class="material-icons">description</span> Case</h3>
            <div class="sb-row"><span>Title:</span><strong>{{ summaryData.s2.title }}</strong></div>
            <div class="sb-row"><span>Type:</span><strong>{{ TYPE_LABELS[summaryData.s2.type] }}</strong></div>
            <div class="sb-row"><span>Priority:</span><strong>{{ summaryData.s2.priority }}</strong></div>
            <div class="sb-row"><span>Our role:</span><strong>{{ summaryData.s2.ourRole }}</strong></div>
            <div class="sb-row"><span>Exposure:</span><strong>{{ formatCurrency(summaryData.s2.exposureAmount || 0) }}</strong></div>
            <div class="sb-row" *ngIf="summaryData.s2.court"><span>Court:</span><strong>{{ summaryData.s2.court }}</strong></div>
            <div class="sb-row" *ngIf="summaryData.s2.courtCaseNumber"><span>Court case #:</span><strong>{{ summaryData.s2.courtCaseNumber }}</strong></div>
            <div class="sb-row"><span>Assigned to:</span><strong>{{ summaryData.s2.assignedTo }}</strong></div>
            <div class="sb-row" *ngIf="summaryData.s2.expectedCloseDate"><span>Expected close:</span><strong>{{ summaryData.s2.expectedCloseDate }}</strong></div>
          </div>

          <div class="summary-block">
            <h3><span class="material-icons">group</span> Parties</h3>
            <div class="sb-row"><span>Opposing party:</span><strong>{{ summaryData.s3.opposingParty }}</strong></div>
            <div class="sb-row" *ngIf="summaryData.s3.opposingCounsel"><span>Opposing counsel:</span><strong>{{ summaryData.s3.opposingCounsel }}</strong></div>
            <div class="sb-row"><span>Additional parties:</span><strong>{{ summaryData.additionalPartyCount }}</strong></div>
          </div>

          <div class="summary-block" *ngIf="!summaryData.s4.skipFirstEvent && summaryData.s4.scheduledAt">
            <h3><span class="material-icons">event</span> First Event</h3>
            <div class="sb-row"><span>{{ EVENT_LABELS[summaryData.s4.type] }}:</span><strong>{{ summaryData.s4.title }}</strong></div>
            <div class="sb-row"><span>When:</span><strong>{{ summaryData.s4.scheduledAt }}</strong></div>
            <div class="sb-row" *ngIf="summaryData.s4.location"><span>Location:</span><strong>{{ summaryData.s4.location }}</strong></div>
            <div class="sb-row"><span>Reminders:</span><strong>{{ summaryData.s4.reminderDaysBefore }} days before</strong></div>
          </div>

          <div class="summary-block muted" *ngIf="summaryData.s4.skipFirstEvent">
            <h3><span class="material-icons">event_busy</span> First Event</h3>
            <div class="sb-row"><em>Skipped — to be scheduled later.</em></div>
          </div>
        </div>
      </div>

      <!-- Navigation buttons -->
      <div class="wizard-nav">
        <button class="btn btn-secondary" (click)="prev()" *ngIf="currentStep > 1" [disabled]="submitting">
          <span class="material-icons">arrow_back</span> Back
        </button>
        <div class="spacer"></div>
        <button class="btn btn-primary"
                (click)="next()"
                *ngIf="currentStep < totalSteps">
          Next <span class="material-icons">arrow_forward</span>
        </button>
        <button class="btn btn-primary submit-btn"
                (click)="submit()"
                *ngIf="currentStep === totalSteps"
                [disabled]="submitting">
          <span class="material-icons" [class.spinning]="submitting">{{ submitting ? 'autorenew' : 'check_circle' }}</span>
          {{ submitting ? 'Creating...' : 'Open Case' }}
        </button>
      </div>
    </section>
  </main>
</div>
